class TCCCartDrawer extends HTMLElement {
    constructor() {
        super();
        // Bind the method once to maintain reference
        this.handleOpenCart = () => this.openCart();
        this.handleCartItemClick = (e) => this.handleCartItemInteractions(e);
        this.handleCheckout = (e) => { 
            console.log('Checkout fired!');
            e.stopPropagation();
            window.location.href = '/checkout'; 
        };
        this.sectionId = this.getAttribute('data-section-id');

        // ── Variant Popup state ──
        this.gvsMax = 2;
        this.gvsSelections = {};  // { variantId: qty }
        this.gvsVariants = [];
        this.gvsItemKey = null;
    }

    connectedCallback() {
        // Cache DOM elements
        this.cartItemsContainer = this.querySelector('.g-cart-items');
        this.cartCountEl = this.querySelector('.cart-count');
        this.cartTotalEl = this.querySelector('.cart-total');
        this.cartContent = this.querySelector('#g-cart-content');
        this.cartDrawer = this.querySelector('#g-cart-content .g-cart-drawer');
        this.cartOverlay = this.querySelector('.g-cart-overlay');
        this.closeButton = this.querySelector('.g-close');
        this.upsellButtons = this.querySelectorAll('.g-cart-upsell-content button');
        this.checkoutBtn = this.querySelector('.g-cart-checkout');

        // Read Liquid-rendered values from data attributes, these are used for the FREEGIFT algorithm
        this.freeProductId = parseInt(this.dataset.freeProductId);
        this.freeProductVariantId = parseInt(this.dataset.freeProductVariantId);
        this.sectionId = this.dataset.sectionId;
        this.freegiftEnabled = this.dataset.freegiftEnabled === 'true';
        this.freegiftThreshold = parseInt(this.dataset.freegiftThreshold) || 0;
        this.stocklabel = this.dataset.stocklabel;

        // Pulse
        this.pulseTimer = this.querySelector("#g-bottom-text");
        this.pulseMin = parseInt(this.getAttribute('data-timer-min'));
        this.pulseMax = parseInt(this.getAttribute('data-timer-max'));

        if (this.pulseTimer) {
            const randomPeople = Math.floor(Math.random() * (this.pulseMax - this.pulseMin + 1)) + this.pulseMin;
            this.pulseTimer.innerHTML = `<p><strong>${randomPeople}</strong> ${this.stocklabel}</p>`;
        }
        

        // Toggle Shipping Insurance
        this.productToggle = this.querySelector('.g-cart-cta .product-toggle input[type="checkbox"]');
        if (this.productToggle) {
            this.handleProductToggle = async (e) => {
                const isChecked = e.target.checked;
                const variantId = parseInt(e.target.dataset.vid);

                if (isChecked) {
                    await fetch('/cart/add.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                        body: JSON.stringify({
                            id: variantId,
                            quantity: 1,
                            sections: `${this.sectionId},get-cart-bubble`
                        })
                    });
                    this.refreshCart();
                } else {
                    // First find the line item key
                    const cartRes = await fetch('/cart.js');
                    const cart = await cartRes.json();
                    const item = cart.items.find(i => i.variant_id === variantId);

                    if (item) {
                        await fetch('/cart/change.js', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                id: item.key,
                                quantity: 0,
                                sections: `${this.sectionId},get-cart-bubble`
                            })
                        });
                        this.refreshCart();
                    }
                }
            };

            this.productToggle.removeEventListener('change', this.handleProductToggle);
            this.productToggle.addEventListener('change', this.handleProductToggle);
        }

        // Additional Days
        this.deliveryDateEl = this.querySelector('#g-delivery-date');
        this.additionalDays = parseInt(this.dataset.additionalDays) || 0;

        if (this.additionalDays > 0) {
            this.updateDeliveryDate(this.additionalDays);
        }


        // Checkout Button
        // if (this.checkoutBtn) {
        //     this.checkoutBtn.removeEventListener('click', this.handleCheckout);
        //     this.handleCheckout = () => { window.location.href = '/checkout'; };
        //     this.checkoutBtn.addEventListener('click', this.handleCheckout);
        // }


        // Remove any existing listener before adding a new one
        document.removeEventListener('gOpenCart', this.handleOpenCart);
        document.addEventListener('gOpenCart', this.handleOpenCart);

        // Add close button listener (remove old one first if it exists)
        if (this.closeButton) {
            this.closeButton.removeEventListener('click', this.boundCloseCart);
            this.boundCloseCart = () => this.closeCart();
            this.closeButton.addEventListener('click', this.boundCloseCart);
        }

        if (this.cartOverlay) {
            this.cartOverlay.removeEventListener('click', this.boundCloseCartOverlay);
            this.boundCloseCartOverlay = () => this.closeCart();
            this.cartOverlay.addEventListener('click', this.boundCloseCartOverlay);
        }

        // Use event delegation for cart items
        if (this.cartItemsContainer) {
            // Remove listener before adding to prevent duplicates
            this.cartItemsContainer.removeEventListener('click', this.handleCartItemClick);
            this.cartItemsContainer.addEventListener('click', this.handleCartItemClick);
        }

        // Remove old listeners and re-add for each upsell button
        this.upsellButtons.forEach(btn => {
            btn.removeEventListener('click', this.handleUpsellClick);
            btn.addEventListener('click', this.handleUpsellClick);
        });

        this.initSwiper();
        this.initSelectListeners();

        // ── Variant Popup ──
        this.initVariantPopup();
    }

    disconnectedCallback() {
        // Clean up when element is removed
        document.removeEventListener('gOpenCart', this.handleOpenCart);
        if (this.cartItemsContainer) {
            this.cartItemsContainer.removeEventListener('click', this.handleCartItemClick);
        }
    }

    // ─────────────────────────────────────────────
    //  VARIANT POPUP
    // ─────────────────────────────────────────────

    initVariantPopup() {
        // Bind popup buttons — HTML already exists in your Liquid
        const overlay = document.getElementById('gvsOverlay');
        const closeBtn = document.getElementById('gvsClose');
        const addBtn   = document.getElementById('gvsAddBtn');

        if (overlay) overlay.onclick = () => this._closeVariantPopup();
        if (closeBtn) closeBtn.onclick = () => this._closeVariantPopup();
        if (addBtn)   addBtn.onclick   = () => this._handleVariantAdd();

        // Delegate qty-option clicks via the cart items container
        if (this.cartItemsContainer) {
            this.cartItemsContainer.removeEventListener('click', this._handleQtyOptionClick);
            this._handleQtyOptionClick = (e) => {
                const optionEl = e.target.closest('.g-cart-qty-option');
                if (!optionEl) return;
                const cartItem = optionEl.closest('.g-cart-item');
                if (!cartItem) return;
                this._openVariantPopup(optionEl, cartItem);
            };
            this.cartItemsContainer.addEventListener('click', this._handleQtyOptionClick);
        }
    }

    async _openVariantPopup(optionEl, cartItem) {
        this.gvsMax = parseInt(optionEl.dataset.maxSelection) || 2;
        this.gvsSelections = {};
        this.gvsItemKey = cartItem.dataset.key;

        // Populate header text (map to IDs you already have in your HTML)
        const titleEl    = document.getElementById('gvsProductTitle');
        const maxLabelEl = document.getElementById('gvsMaxLabel');
        const maxCountEl = document.getElementById('gvsMaxCount');
        const dealTextEl = document.getElementById('gvsDealText');
        const errorEl    = document.getElementById('gvsError');
        const addBtn     = document.getElementById('gvsAddBtn');

        if (titleEl)    titleEl.textContent    = cartItem.dataset.productTitle || 'Choose Your Variants';
        if (maxLabelEl) maxLabelEl.textContent = this.gvsMax;
        if (maxCountEl) maxCountEl.textContent = this.gvsMax;
        if (dealTextEl) dealTextEl.textContent = optionEl.textContent.trim();
        if (errorEl)    errorEl.style.display  = 'none';
        if (addBtn)     addBtn.disabled        = true;

        // Show popup
        document.getElementById('gvsOverlay')?.classList.add('open');
        document.getElementById('gvsPopup')?.classList.add('open');
        document.body.style.overflow = 'hidden';

        // Load spinner state into list
        const list = document.getElementById('gvsList');
        if (list) list.innerHTML = '<div style="text-align:center;padding:20px;color:#888">Loading variants…</div>';

        try {
            const res  = await fetch(`/products/${cartItem.dataset.productHandle}.js`);
            const data = await res.json();
            this.gvsVariants = data.variants.map(v => ({
                id: v.id,
                title: v.title,
                price: v.price,
                priceFormatted: '$' + (v.price / 100).toFixed(2).replace(/\.00$/, ''),
                available: v.available
            }));
            this._renderVariantList();
        } catch {
            if (list) list.innerHTML = '<div style="text-align:center;padding:20px;color:#c0392b">Failed to load variants.</div>';
        }
    }

    _closeVariantPopup() {
        document.getElementById('gvsOverlay')?.classList.remove('open');
        document.getElementById('gvsPopup')?.classList.remove('open');
        document.body.style.overflow = '';
    }

    _gvsTotal() {
        return Object.values(this.gvsSelections).reduce((s, q) => s + q, 0);
    }

    _renderVariantList() {
        const list = document.getElementById('gvsList');
        if (!list) return;
        list.innerHTML = '';

        const total = this._gvsTotal();

        this.gvsVariants.forEach(v => {
            const qty   = this.gvsSelections[v.id] || 0;
            const atCap = total >= this.gvsMax && qty === 0;

            const row = document.createElement('div');
            row.className = 'gvs-variant-row' + (qty > 0 ? ' has-qty' : '');
            row.innerHTML = `
                <div class="gvs-variant-name">${v.title}</div>
                <div class="gvs-variant-price">${v.priceFormatted}</div>
                <div class="gvs-qty-wrap">
                    <div class="gvs-qty-btn gvs-minus" data-id="${v.id}">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M5 12h14"/>
                        </svg>
                    </div>
                    <div class="gvs-qty-num">${qty}</div>
                    <div class="gvs-qty-btn gvs-plus${atCap ? ' gvs-disabled' : ''}" data-id="${v.id}">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
                        </svg>
                    </div>
                </div>
            `;
            list.appendChild(row);
        });

        // Bind +/- buttons
        list.querySelectorAll('.gvs-minus').forEach(btn =>
            btn.addEventListener('click', () => this._gvsAdjust(parseInt(btn.dataset.id), -1))
        );
        list.querySelectorAll('.gvs-plus:not(.gvs-disabled)').forEach(btn =>
            btn.addEventListener('click', () => this._gvsAdjust(parseInt(btn.dataset.id), 1))
        );

        // Update counter
        const selCount = document.getElementById('gvsSelectedCount');
        if (selCount) selCount.textContent = total;

        const addBtn = document.getElementById('gvsAddBtn');
        if (addBtn) addBtn.disabled = total === 0;
    }

    _gvsAdjust(variantId, delta) {
        const cur  = this.gvsSelections[variantId] || 0;
        if (delta > 0 && this._gvsTotal() >= this.gvsMax) return;
        const next = Math.max(0, cur + delta);
        if (next === 0) delete this.gvsSelections[variantId];
        else this.gvsSelections[variantId] = next;
        this._renderVariantList();
    }

    async _handleVariantAdd() {
        const items = Object.entries(this.gvsSelections)
            .filter(([, q]) => q > 0)
            .map(([id, qty]) => ({ id: parseInt(id), quantity: qty }));

        if (!items.length) return;

        const addBtn  = document.getElementById('gvsAddBtn');
        const errorEl = document.getElementById('gvsError');

        addBtn.classList.add('loading');
        addBtn.disabled = true;
        if (errorEl) errorEl.style.display = 'none';
        console.log(items);
        const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

        try {
            // 1. Remove original item first
            if (this.gvsItemKey) {
                const cartRes = await fetch('/cart.js');
                const cart = await cartRes.json();
                const lineIndex = cart.items.findIndex(i => i.key === this.gvsItemKey);

                if (lineIndex !== -1) {
                    await fetch('/cart/change.js', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            line: lineIndex + 1,
                            quantity: 0
                        })
                    });
                }
            }

            // 2. Add selected variants
            await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items })
            });

            // 3. Apply discount
            const DISCOUNT_BY_COUNT = {
                1: '1 Bathroom',   
                2: '2 Bathrooms',
                3: '3 Bathrooms'  
            };

            const code = DISCOUNT_BY_COUNT[totalQuantity];
            if (code) {
                await fetch(`/discount/${encodeURIComponent(code)}`);
            }

            addBtn.classList.remove('loading');
            addBtn.disabled = false;
            // 3. Close popup & refresh drawer (no page reload)
            this._closeVariantPopup();
            this.refreshCart();
        } catch {
            if (errorEl) errorEl.style.display = 'block';
            addBtn.classList.remove('loading');
            addBtn.disabled = false;
        }
    }

    // ─────────────────────────────────────────────
    //  EVERYTHING BELOW IS UNCHANGED
    // ─────────────────────────────────────────────

    getOrdinalSuffix(day) {
        if (day > 3 && day < 21) return 'th';
        switch (day % 10) {
            case 1: return 'st';
            case 2: return 'nd';
            case 3: return 'rd';
            default: return 'th';
        }
    }

    updateDeliveryDate(daysToAdd) {
        if (!this.deliveryDateEl) return;

        const today = new Date();
        const deliveryDate = new Date(today);
        deliveryDate.setDate(today.getDate() + daysToAdd);

        const month = deliveryDate.toLocaleString('default', { month: 'short' });
        const day = deliveryDate.getDate();
        const suffix = this.getOrdinalSuffix(day);

        this.deliveryDateEl.textContent = `${month} ${day}${suffix}`;
    }



    // TIER FREE GIFT
    async checkFreeProduct() {
        const res = await fetch('/cart.js');
        const cart = await res.json();

        let hasSubscription = false;
        let freeProductExisting = false;
        let freeProductLineKey = null;

        cart.items.forEach((item) => {
            if (item.selling_plan_allocation) hasSubscription = true;
            if (item.product_id === this.freeProductId) {
                freeProductExisting = true;
                freeProductLineKey = item.key;
            }
        });

        console.log(hasSubscription, freeProductExisting);
        console.log(this.freegiftEnabled, this.freegiftThreshold);

        if (this.freegiftEnabled && cart.total_price >= this.freegiftThreshold) {
            if (!freeProductExisting) {
                await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                    body: JSON.stringify({
                        id: this.freeProductVariantId,
                        quantity: 1,
                        sections: `${this.sectionId},get-cart-bubble`
                    })
                });
                this.refreshCart();
            }
        } else {
            if (freeProductExisting) {
                await fetch('/cart/change.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        id: freeProductLineKey,
                        quantity: 0,
                        sections: `${this.sectionId},get-cart-bubble`
                    })
                });
                this.refreshCart();
            }
        }
    }


    // AUTO FREEGIFT
    async checkAutoFreeGifts(cart) {
        const cartProductIds = cart.items.map(i => i.product_id);
        const hasTriggerProduct = this.triggerProductIds.some(id => cartProductIds.includes(id));

        if (hasTriggerProduct) {
            // Filter only gifts not already in cart
            const toAdd = this.freegiftVariantIds.filter((variantId, i) => {
                return !cartProductIds.includes(this.freegiftProductIds[i]);
            }).map(id => ({ id, quantity: 1, properties: { _free_gift: true } }));

            if (toAdd.length > 0) {
                await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                    body: JSON.stringify({ items: toAdd })
                });
            }
        } else {
            // Build updates object { key: 0 } for all free gifts in cart
            const updates = {};
            cart.items.forEach(item => {
                if (this.freegiftProductIds.includes(item.product_id)) {
                    updates[item.key] = 0;
                }
            });

            if (Object.keys(updates).length > 0) {
                await fetch('/cart/update.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ updates })
                });
            }
        }

        this.refreshCart();
    }


    handleCartItemInteractions(e) {
        const target = e.target;
        const item = target.closest('.g-cart-item');
        if (!item) return;

        const quantity = item.querySelector(".g-ci-quantity");

        // Check if target or its parent is the plus button
        const plusButton = target.closest('.g-ci-quantity-plus');
        const minusButton = target.closest('.g-ci-quantity-minus');
        const removeButton = target.closest('.g-remove');
        const checkbox = target.closest('.g-ci-sub-check');

        // Handle checkbox clicks
        if (checkbox) {
            const isChecked = item.classList.toggle("active");
            checkbox.setAttribute("aria-checked", isChecked ? "true" : "false");
            item.dataset.checked = isChecked;

            console.log("Item checked:", isChecked);
            const defaultSP = checkbox.getAttribute("data-default-sp");
            const currentSP = checkbox.getAttribute("data-current-sp");
            const cbLine = checkbox.getAttribute("data-line");
            const inputQty = parseInt(quantity.innerHTML);
            console.log(defaultSP, currentSP);
            
            if(isChecked){
                if(currentSP == ""){
                    updateCartSellingPlan(cbLine, inputQty, defaultSP);
                }
            }else{
                if(currentSP){
                    updateCartSellingPlan(cbLine, inputQty, "");
                }
            }
        }

        // Handle plus button
        if (plusButton) {
            const key = parseInt(plusButton.getAttribute("data-line"));
            let inputQty = parseInt(quantity.innerHTML);
            inputQty += 1;
            quantity.innerHTML = inputQty;
            this.updateCartQuantity(key, inputQty);
        }

        // Handle minus button
        if (minusButton) {
            const key = parseInt(minusButton.getAttribute("data-line"));
            let inputQty = parseInt(quantity.innerHTML);
            inputQty -= 1;
            quantity.innerHTML = inputQty;
            this.updateCartQuantity(key, inputQty);
        }

        // Handle remove button
        if (removeButton) {
            const key = parseInt(removeButton.getAttribute("data-line"));
            this.updateCartQuantity(key, 0);
        }
    }

    initSelectListeners() {
        // Handle variant selects separately (change events)
        this.querySelectorAll(".g-cart-item-subscription select").forEach(select => {
            // Remove existing listener if any
            const newSelect = select.cloneNode(true);
            select.parentNode.replaceChild(newSelect, select);
            
            newSelect.addEventListener("change", async(e) => {
                const sp = e.target.value;
                const key = e.target.getAttribute("data-key");
                const qty = e.target.getAttribute("data-qty");

                fetch('/cart/change.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        id: key,
                        quantity: qty,
                        selling_plan: sp
                    })
                })
                .then(res => res.json())
                .then(cart => this.refreshCart() )
                .catch(err => console.error(err));
            });
        });
    }

    handleUpsellClick = (e) => {
        const btn = e.currentTarget;
        const vid = btn.getAttribute("data-vid");
        fetch('/cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify({
                id: vid,
                quantity: 1
            })
        })
        .then(response => response.json())
        .then(data => {
            this.refreshCart();
        })
        .catch((error) => {
            console.error('Error adding to cart:', error);
        });
    }

    async initSwiper() {
        await this.loadSwiperAssets();
        new Swiper('.g-cart-upsells-slider', {
            grabCursor: true,
            slidesPerView: 1,
            spaceBetween: 10,
            navigation: {
                nextEl: '.g-cus-next',
                prevEl: '.g-cus-prev',
            }
        });
    }

    async loadSwiperAssets() {
        const promises = [];

        if (!window.Swiper) {
          promises.push(this.loadScript('https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.js'));
        }

        if (!document.querySelector('link[href*="swiper"]')) {
          promises.push(this.loadStylesheet('https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css'));
        }

        if (promises.length > 0) {
          await Promise.all(promises);
        }
      }

      loadScript(src) {
        return new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = src;
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
      }

      loadStylesheet(href) {
        return new Promise((resolve, reject) => {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = href;
          link.onload = resolve;
          link.onerror = reject;
          document.head.appendChild(link);
        });
      }

    async updateCartQuantity(key, qty){
        fetch('/cart/change.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify({
                line: key,
                quantity: qty
            })
        })
        .then(response => response.json())
        .then(data => {
            this.refreshCart();
        })
        .catch(error => {
            console.error('Error removing item:', error);
        });
    }

    async refreshCart() {
        // Use the section ID instead of the file name
        const res = await fetch(`/?sections=${this.sectionId}`);
        const data = await res.json();

        const parser = new DOMParser();
        const doc = parser.parseFromString(data[this.sectionId], 'text/html');
        const cart_content = doc.querySelector('#g-cart-content .g-cart-drawer');
        
        if (cart_content && this.cartContent) {
            this.cartDrawer.innerHTML = cart_content.innerHTML;
        }
        
        console.log("From tcc-cart-drawer", this.cartContent);

        this.checkFreeProduct();

        // Re-initialize after content update
        this.connectedCallback();
    }

    openCart() {
        this.style.visibility = "visible";
        
        if (this.cartContent) {
            this.cartContent.classList.add('g-cart-open');
        }
        this.refreshCart();
    }

    closeCart() {
        this.style.visibility = "hidden";

        if (this.cartContent) {
            this.cartContent.classList.remove('g-cart-open');
        }
        document.querySelector("body").style.overflow = "auto";
    }
}

customElements.define('tcc-cart-drawer', TCCCartDrawer);


// INTERCEPT /cart click
document.addEventListener("click", function (e) {
    const link = e.target.closest("a");
    if (!link) return;

    if (link.href.includes("/cart")) {
        e.preventDefault();
        document.dispatchEvent(new Event("gOpenCart"));
    }
}, true); // 👈 capture phase so it fires before the app intercepts

// Listen for all form submissions on the page
// document.addEventListener('submit', function(event) {
//     const form = event.target;

//     // Check if the form action includes "/cart/add" (or any other specific condition)
//     if (form.action.includes('/cart/add')) {
//         event.stopImmediatePropagation();
//         event.preventDefault(); // Prevent the default form submission

//         console.log('Intercepted form submission to /cart/add');

//         // Create FormData object to get all form inputs
//         const formData = new FormData(form);
//         console.log(formData);

//         // Use fetch to send the form data via POST
//         fetch(form.action, {
//             method: 'POST',
//             body: formData,
//         })
//         .then(response => {
//             // Check if the response is ok (status 200-299)
//             if (!response.ok) {
//                 throw new Error(`HTTP error! Status: ${response.status}`);
//             }
//             return response.text(); // Read the response as text
//         })
//         .then(text => {
//             // Trigger custom 'cartshow' event after successful form submission
//             document.dispatchEvent(new Event("gOpenCart"));
//         })
//         .catch(error => {
//             console.error('Error adding item to cart:', error);
//         });
//     }
// }, true);

document.addEventListener('DOMContentLoaded', () => {
    window.SideCart = {
        updateSideCart: function() {
            if(document.querySelector(".show-loading-overlay #loading-overlay"))
                document.querySelector(".show-loading-overlay #loading-overlay").style.display = "none";
            document.dispatchEvent(new Event("gOpenCart"));
        }
    };
});
